import { useState, useEffect } from 'react';
import { BookOpen, Save, Loader2, CheckCircle2, XCircle, ShieldCheck } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Switch } from '@/components/ui/switch';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { useToast } from '@/components/ui/use-toast';
import { vaultApi } from '@/services/api';
import type { VaultConfig } from '@/types/notes.types';

export function VaultSettingsPage() {
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [validating, setValidating] = useState(false);

  const [config, setConfig] = useState<VaultConfig | null>(null);
  const [form, setForm] = useState({
    webdavUrl: 'https://dav.jianguoyun.com/dav/',
    webdavUser: '',
    webdavPass: '',
    basePath: '/笔记同步/',
    enabled: false,
  });

  // 验证结果
  const [verifyStatus, setVerifyStatus] = useState<'success' | 'error' | null>(null);
  const [verifyMessage, setVerifyMessage] = useState('');
  const [isVerified, setIsVerified] = useState(false);

  useEffect(() => {
    loadConfig();
  }, []);

  const loadConfig = async () => {
    setLoading(true);
    try {
      const res = await vaultApi.getConfig();
      const data = res.data.data;
      setConfig(data);
      setForm({
        webdavUrl: data.webdavUrl,
        webdavUser: data.webdavUser || '',
        webdavPass: '',
        basePath: data.basePath,
        enabled: data.enabled,
      });
      // 如果已有配置且 enabled，认为已验证过（允许直接保存开关）
      setIsVerified(data.enabled);
    } catch {
      toast({ title: '获取 Vault 配置失败', variant: 'destructive' });
    } finally {
      setLoading(false);
    }
  };

  const handleChange = (field: string, value: string | boolean) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    // 修改任何字段后重置验证状态（除了开关）
    if (field !== 'enabled') {
      setIsVerified(false);
      setVerifyStatus(null);
      setVerifyMessage('');
    }
  };

  const handleValidate = async () => {
    if (!form.webdavUrl || !form.webdavUser || !form.webdavPass) {
      toast({ title: '请填写完整的 WebDAV 地址、用户名和密码', variant: 'destructive' });
      return;
    }

    setValidating(true);
    setVerifyStatus(null);
    try {
      const res = await vaultApi.validateConfig({
        webdavUrl: form.webdavUrl,
        webdavUser: form.webdavUser,
        webdavPass: form.webdavPass,
      });
      const data = res.data;
      if (data.valid) {
        setVerifyStatus('success');
        setVerifyMessage(data.message);
        setIsVerified(true);
        toast({ title: data.message });
      } else {
        setVerifyStatus('error');
        setVerifyMessage(data.message);
        setIsVerified(false);
        toast({ title: data.message, variant: 'destructive' });
      }
    } catch (err: any) {
      const message = err?.response?.data?.message || '验证失败';
      setVerifyStatus('error');
      setVerifyMessage(message);
      setIsVerified(false);
      toast({ title: message, variant: 'destructive' });
    } finally {
      setValidating(false);
    }
  };

  const handleSave = async () => {
    // 如果要启用同步，必须先验证通过
    if (form.enabled && !isVerified && !config?.enabled) {
      toast({ title: '请先验证 WebDAV 凭据，验证通过后才能启用同步', variant: 'destructive' });
      return;
    }

    // 如果要保存新密码但没有验证过，提示先验证
    if (form.webdavPass && !isVerified) {
      toast({ title: '密码已修改，请先验证凭据', variant: 'destructive' });
      return;
    }

    setSaving(true);
    try {
      const payload: Record<string, any> = {
        webdavUrl: form.webdavUrl,
        webdavUser: form.webdavUser,
        basePath: form.basePath,
        enabled: form.enabled,
      };
      // 只有输入了新密码才提交
      if (form.webdavPass) {
        payload.webdavPass = form.webdavPass;
      }
      const res = await vaultApi.updateConfig(payload);
      const data = res.data.data;
      setConfig(data);
      setForm((prev) => ({
        ...prev,
        webdavPass: '',
        enabled: data.enabled,
      }));
      setIsVerified(data.enabled);
      toast({ title: res.data.message || 'Vault 配置已保存' });
    } catch (err: any) {
      const message = err?.response?.data?.message || '保存失败';
      toast({ title: message, variant: 'destructive' });
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-blue-500" />
      </div>
    );
  }

  const hasExistingConfig = Boolean(config?.webdavUser);

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Obsidian Vault 同步设置</h1>

      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <BookOpen className="w-5 h-5" />
            坚果云 WebDAV 配置
          </CardTitle>
          <CardDescription>
            配置坚果云 WebDAV 后，您的笔记将自动同步到 Obsidian Vault（只读镜像）
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {hasExistingConfig && (
            <div className="flex items-center gap-2 text-sm text-green-700 bg-green-50 px-3 py-2 rounded-md">
              <CheckCircle2 className="w-4 h-4" />
              <span>
                已配置坚果云账号：{config?.webdavUser}
                {config?.enabled ? '（同步已启用）' : '（同步已禁用）'}
              </span>
            </div>
          )}

          <div className="space-y-2">
            <Label htmlFor="webdav-url">WebDAV 地址</Label>
            <Input
              id="webdav-url"
              type="url"
              placeholder="https://dav.jianguoyun.com/dav/"
              value={form.webdavUrl}
              onChange={(e) => handleChange('webdavUrl', e.target.value)}
            />
            <p className="text-xs text-gray-500">坚果云默认地址，通常无需修改</p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="webdav-user">坚果云账号</Label>
            <Input
              id="webdav-user"
              type="text"
              placeholder="请输入坚果云邮箱/手机号"
              value={form.webdavUser}
              onChange={(e) => handleChange('webdavUser', e.target.value)}
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="webdav-pass">应用密码</Label>
            <Input
              id="webdav-pass"
              type="password"
              placeholder={
                hasExistingConfig
                  ? '输入新密码可更换，留空保持不变'
                  : '请输入坚果云应用密码（非登录密码）'
              }
              value={form.webdavPass}
              onChange={(e) => handleChange('webdavPass', e.target.value)}
            />
            <p className="text-xs text-gray-500">
              在坚果云「安全设置 → 第三方应用管理」中生成应用密码
            </p>
          </div>

          <div className="space-y-2">
            <Label htmlFor="base-path">同步文件夹路径</Label>
            <Input
              id="base-path"
              type="text"
              placeholder="/笔记同步/"
              value={form.basePath}
              onChange={(e) => handleChange('basePath', e.target.value)}
            />
            <p className="text-xs text-gray-500">笔记在坚果云上的存放文件夹，修改后需手动迁移已有文件</p>
          </div>

          {verifyStatus && (
            <div
              className={`flex items-center gap-2 text-sm px-3 py-2 rounded-md ${
                verifyStatus === 'success'
                  ? 'text-green-700 bg-green-50'
                  : 'text-red-700 bg-red-50'
              }`}
            >
              {verifyStatus === 'success' ? (
                <CheckCircle2 className="w-4 h-4" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              <span>{verifyMessage}</span>
            </div>
          )}

          <div className="flex gap-3">
            <Button
              onClick={handleValidate}
              disabled={validating || !form.webdavUrl || !form.webdavUser || !form.webdavPass}
              variant="outline"
              className="gap-2"
            >
              {validating ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <ShieldCheck className="w-4 h-4" />
              )}
              验证凭据
            </Button>
            <Button onClick={handleSave} disabled={saving} className="gap-2">
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
              保存配置
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>同步开关</CardTitle>
          <CardDescription>启用后，您的笔记将在保存时自动同步到 Obsidian Vault</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between">
            <div className="space-y-0.5">
              <Label htmlFor="vault-enabled">启用 Vault 同步</Label>
              <p className="text-xs text-gray-500">
                关闭后笔记不再自动推送，但已有文件保留在坚果云上
              </p>
            </div>
            <Switch
              id="vault-enabled"
              checked={form.enabled}
              onCheckedChange={(checked) => handleChange('enabled', checked)}
            />
          </div>

          <Button onClick={handleSave} disabled={saving} className="gap-2">
            {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
            保存开关状态
          </Button>
        </CardContent>
      </Card>
    </div>
  );
}
